import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Watchlist {
  id: string;
  name: string;
  number_of_stocks: number;
  watchlist_type?: 'default' | 'system_generated';
}

interface WatchlistMultiFilterProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  watchlists: Watchlist[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export default function WatchlistMultiFilter({
  value,
  onChange,
  watchlists,
  label = "Watchlists",
  placeholder = "Select watchlists...",
  disabled = false
}: WatchlistMultiFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse selected IDs from comma-separated string
  const selectedIds = useMemo(() => {
    return value ? value.split(',').filter(id => id.trim()) : [];
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Separate watchlists by type and sort alphabetically
  const { defaultWatchlists, systemWatchlists, hasResults } = useMemo(() => {
    const filteredWatchlists = watchlists.filter(watchlist =>
      watchlist.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const defaultList = filteredWatchlists
      .filter(watchlist => watchlist.watchlist_type === 'default')
      .sort((a, b) => a.name.localeCompare(b.name));

    const systemList = filteredWatchlists
      .filter(watchlist => watchlist.watchlist_type === 'system_generated')
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      defaultWatchlists: defaultList,
      systemWatchlists: systemList,
      hasResults: filteredWatchlists.length > 0
    };
  }, [watchlists, searchTerm]);

  const handleCheckboxChange = (watchlistId: string, checked: boolean) => {
    let newSelectedIds: string[];

    if (checked) {
      newSelectedIds = [...selectedIds, watchlistId];
    } else {
      newSelectedIds = selectedIds.filter(id => id !== watchlistId);
    }

    // Create a synthetic event to maintain consistency with other filters
    const syntheticEvent = {
      target: {
        name: 'watchlist_ids',
        value: newSelectedIds.join(',')
      }
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  const getDisplayText = () => {
    if (selectedIds.length === 0) return placeholder;
    if (selectedIds.length === 1) {
      const watchlist = watchlists.find(w => w.id === selectedIds[0]);
      return watchlist?.name || 'Unknown';
    }
    return `${selectedIds.length} watchlists selected`;
  };

  const getTotalStocks = () => {
    return watchlists
      .filter(w => selectedIds.includes(w.id))
      .reduce((total, w) => total + w.number_of_stocks, 0);
  };

  const handleSelectAll = () => {
    const allIds = watchlists.map(w => w.id).join(',');
    const syntheticEvent = {
      target: { name: 'watchlist_ids', value: allIds }
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
  };

  const handleSelectDefault = () => {
    const defaultIds = defaultWatchlists.map(w => w.id).join(',');
    const syntheticEvent = {
      target: { name: 'watchlist_ids', value: defaultIds }
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
  };

  const handleSelectSystem = () => {
    const systemIds = systemWatchlists.map(w => w.id).join(',');
    const syntheticEvent = {
      target: { name: 'watchlist_ids', value: systemIds }
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
  };

  const handleClearAll = () => {
    const syntheticEvent = {
      target: { name: 'watchlist_ids', value: '' }
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
  };

  const WatchlistItem = ({ watchlist }: { watchlist: Watchlist }) => {
    const isSelected = selectedIds.includes(watchlist.id);

    return (
      <div
        className={`group flex items-start gap-2 p-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-all duration-200 border-l-3 ${
          isSelected
            ? 'border-l-emerald-500 bg-emerald-500/10'
            : 'border-l-transparent hover:border-l-emerald-300'
        }`}
        onClick={() => handleCheckboxChange(watchlist.id, !isSelected)}
      >
        <div className={`flex-shrink-0 w-4 h-4 rounded border transition-all duration-200 mt-0.5 ${
          isSelected
            ? 'bg-emerald-600 border-emerald-600'
            : 'border-zinc-300 dark:border-zinc-600 group-hover:border-emerald-400'
        }`}>
          {isSelected && (
            <CheckIcon className="w-3 h-3 text-white m-0.5" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className={`text-xs font-normal text-zinc-900 dark:text-zinc-100 transition-colors leading-tight break-words ${
              isSelected ? 'text-emerald-700 dark:text-emerald-400' : ''
            }`}>
              {watchlist.name}
            </h3>
            <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full font-medium transition-colors ${
              watchlist.watchlist_type === 'system_generated'
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            }`}>
              {watchlist.watchlist_type === 'system_generated' ? 'S' : 'D'}
            </span>
          </div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5">
            {watchlist.number_of_stocks} stocks
          </p>
        </div>
      </div>
    );
  };

  const WatchlistColumn = ({ watchlists, title, type }: {
    watchlists: Watchlist[];
    title: string;
    type: 'default' | 'system_generated'
  }) => {
    if (watchlists.length === 0 && !searchTerm) return null;

    return (
      <div className="flex-1 min-h-0">
        <div className="sticky top-0 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-3 py-2">
          <div className="flex items-center justify-between">
            <h3 className="text-[10px] font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">
              {title}
            </h3>
            <span className={`text-[10px] px-1 py-0.5 rounded-full font-medium ${
              type === 'system_generated'
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            }`}>
              {watchlists.length}
            </span>
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {watchlists.length === 0 ? (
            <div className="p-3 text-center">
              <div className="text-zinc-400 dark:text-zinc-600 mb-1">
                <XMarkIcon className="w-5 h-5 mx-auto" />
              </div>
              <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                {searchTerm ? `No ${title.toLowerCase()} found` : `No ${title.toLowerCase()} available`}
              </p>
            </div>
          ) : (
            watchlists.map((watchlist) => (
              <WatchlistItem key={watchlist.id} watchlist={watchlist} />
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label htmlFor="watchlist_ids" className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1">
        {label}
      </label>

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 text-left bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-xs transition-all duration-200 ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:border-zinc-400 dark:hover:border-zinc-600'
        } ${isOpen ? 'border-emerald-500 ring-2 ring-emerald-500/20' : ''}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <span className={`block font-medium transition-colors ${
              selectedIds.length === 0
                ? 'text-zinc-500 dark:text-zinc-400'
                : 'text-zinc-900 dark:text-zinc-100'
            }`}>
              {getDisplayText()}
            </span>
            {selectedIds.length > 0 && (
              <span className="block text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                {getTotalStocks()} total stocks
              </span>
            )}
          </div>
          <ChevronDownIcon
            className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ml-2 flex-shrink-0 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl max-h-[500px] overflow-hidden sm:min-w-[400px]">
          {watchlists.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-zinc-400 dark:text-zinc-600 mb-2">
                <XMarkIcon className="w-8 h-8 mx-auto" />
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">
                No watchlists available
              </p>
              <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                Create your first watchlist to get started
              </p>
            </div>
          ) : (
            <>
              {/* Enhanced Search Input */}
              <div className="border-b border-zinc-200 dark:border-zinc-800 p-3 bg-zinc-50 dark:bg-zinc-800/60">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Search watchlists..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-8 py-2 text-xs rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="border-b border-zinc-200 dark:border-zinc-800 p-2 bg-white dark:bg-zinc-900">
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="px-2.5 py-1.5 text-xs font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-md hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectDefault}
                    disabled={defaultWatchlists.length === 0}
                    className="px-2.5 py-1.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Default ({defaultWatchlists.length})
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectSystem}
                    disabled={systemWatchlists.length === 0}
                    className="px-2.5 py-1.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    System ({systemWatchlists.length})
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="px-2.5 py-1.5 text-xs font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-md hover:bg-emerald-500/20 transition-colors"
                  >
                    All ({watchlists.length})
                  </button>
                </div>
              </div>

              {/* Two Column Layout with Enhanced Design */}
              <div className="flex max-h-96 overflow-hidden bg-white dark:bg-zinc-900">
                <WatchlistColumn
                  watchlists={defaultWatchlists}
                  title="Default"
                  type="default"
                />
                <div className="w-px bg-zinc-200 dark:bg-zinc-800"></div>
                <WatchlistColumn
                  watchlists={systemWatchlists}
                  title="System"
                  type="system_generated"
                />
              </div>

              {/* Search Results Summary */}
              {searchTerm && hasResults && (
                <div className="border-t border-zinc-200 dark:border-zinc-800 px-3 py-2 bg-zinc-50 dark:bg-zinc-800/60">
                  <p className="text-xs text-zinc-600 dark:text-zinc-400">
                    Found {defaultWatchlists.length + systemWatchlists.length} watchlist{defaultWatchlists.length + systemWatchlists.length !== 1 ? 's' : ''} matching &ldquo;{searchTerm}&rdquo;
                  </p>
                </div>
              )}

              {/* No Results */}
              {searchTerm && !hasResults && (
                <div className="p-6 text-center">
                  <div className="text-zinc-400 dark:text-zinc-600 mb-2">
                    <MagnifyingGlassIcon className="w-8 h-8 mx-auto" />
                  </div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">
                    No watchlists found
                  </p>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                    Try adjusting your search terms
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
} 